import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import { isTrialPeriodEnded } from "common/utils";
import { PLAN_LIMITS, createUnlimitedQuotaStatus } from "./constants";
import { AccountLimits, SubscriptionState, PlanLimits, PlanType, QuotaStatus } from "./types";

/**
 * Subscription 状態全体を取得
 */
export const useSubscription = (): SubscriptionState => {
  return useSelector((state: RootState) => state.subscription);
};

/**
 * 現在のプランを取得
 */
export const usePlan = (): PlanType => {
  return useSelector((state: RootState) => state.subscription.plan);
};

/**
 * プラン制限値を取得（アクティブユーザーのlimitsを返す）
 */
export const usePlanLimits = (): PlanLimits => {
  const subscription = useSelector((state: RootState) => state.subscription);
  const activeUser = useSelector((state: RootState) => state.user.active);

  // subscriptionが未初期化の場合はデフォルト値を返す
  if (!subscription) {
    return PLAN_LIMITS[PlanType.TRIAL];
  }

  // アクティブユーザーのlimitsがある場合はそれを返す
  if (activeUser?.uid && subscription.playerLimits?.[activeUser.uid]) {
    return subscription.playerLimits[activeUser.uid];
  }

  // フォールバック: プランのデフォルト値（undefinedの場合はTRIALを使用）
  return PLAN_LIMITS[subscription.plan] ?? PLAN_LIMITS[PlanType.TRIAL];
};

/**
 * アカウント単位のプラン制限値を取得
 */
export const useAccountLimits = (): AccountLimits => {
  const accountLimits = useSelector((state: RootState) => state.subscription?.accountLimits);
  // フォールバック: デフォルト値
  return (
    accountLimits ?? {
      players: PLAN_LIMITS[PlanType.TRIAL].players,
      assets: PLAN_LIMITS[PlanType.TRIAL].assets,
      chatHistoryLimit: createUnlimitedQuotaStatus(),
    }
  );
};

/**
 * 素材制限値を取得（アクティブユーザーのassets limitsを返す）
 */
export const useAssetLimit = (): QuotaStatus => {
  const subscription = useSelector((state: RootState) => state.subscription);
  const activeUser = useSelector((state: RootState) => state.user.active);

  // subscriptionが未初期化の場合はデフォルト値を返す
  if (!subscription) {
    return PLAN_LIMITS[PlanType.TRIAL].assets;
  }

  // アクティブユーザーのassets limitsがある場合はそれを返す
  if (activeUser?.uid && subscription.playerLimits?.[activeUser.uid]) {
    return subscription.playerLimits[activeUser.uid].assets;
  }

  // フォールバック: アカウント単位のassets limits
  return subscription.accountLimits?.assets ?? PLAN_LIMITS[PlanType.TRIAL].assets;
};

/**
 * 体験プラン (TRIAL) かつ登録から TRIAL_PERIOD_DAYS 日以上経過しているとき true。
 * 保護者登録のお知らせ導線（吹き出し・バナー・パネル内セクション）の表示判定に使う。
 * 登録〜7日未満は false を返し、その間は通常のお知らせを表示する。
 */
export const useShouldShowTrialNotice = (): boolean => {
  const plan = useSelector((state: RootState) => state.subscription.plan);
  const createdAt = useSelector((state: RootState) => state.user.active?.created_at);
  return plan === PlanType.TRIAL && isTrialPeriodEnded(createdAt);
};

/**
 * 機能制限に当たったときのダイアログ表示。
 * TRIAL のときは保護者登録を促す Dialog を出し、それ以外は RestrictedFeatureDialog を出す。
 */
export const useEnforcePlanLimit = () => {
  const plan = usePlan();
  const handleDialog = useDialog();
  return useCallback(
    (source: RestrictedFeatureSource) => {
      if (plan === PlanType.TRIAL) {
        handleDialog({
          type: DialogType.PARENT_REGISTRATION_PROMPT,
          value: true,
          args: { source },
        });
      } else {
        handleDialog({
          type: DialogType.RESTRICTED_FEATURE,
          value: true,
          args: { source },
        });
      }
    },
    [plan, handleDialog],
  );
};

/**
 * アップグレード CTA タップ時のダイアログ表示。
 * UpgradePromotion はプラン別に表示内容を切り替え、TRIAL の場合は保護者登録への導線も内包する。
 */
export const useOpenUpgradeOrPrompt = () => {
  const handleDialog = useDialog();
  return useCallback(() => {
    handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: true });
  }, [handleDialog]);
};
